/**
 * Taxes & Fees wire enums — SCRUM-213 (DS-012 Taxes & Fees BC §5, §6, §7).
 *
 * The taxes & fees domain value objects live in `apps/api` (downstream of this
 * package), so validators cannot import their `*_VALUES` arrays directly — the
 * literal lists below are the WIRE source of truth (CLAUDE.md rule 3), and a
 * parity test in `apps/api`
 * (`taxes-and-fees/interface/__tests__/wire-enum-parity.spec.ts`) asserts they
 * stay byte-identical to the domain VO arrays, so a drift fails CI rather than
 * silently accepting a value the aggregate rejects (or vice-versa).
 *
 * The exception is `REVENUE_DISTRIBUTION_METHOD_VALUES`: that VO lives in the
 * `@artemis/domain` package (ADR 0027), which validators already depend on, so
 * it is imported directly (re-exported below) rather than duplicated — the same
 * pattern as `organization-status.ts` re-exporting `ORGANIZATION_STATUSES`.
 *
 * `TAX_TEXT_MAX_LENGTH` / `FEE_TEXT_MAX_LENGTH` mirror the domain text policies
 * (both 120 codepoints) and are parity-tested the same way.
 */
export const TAX_COMPUTE_FORM_VALUES = ['Percentage', 'Fixed'];
export const TAX_CADENCE_VALUES = ['Night', 'Stay'];
export const TAX_RESOLUTION_MODE_VALUES = ['manual'];
export const PERCENTAGE_BASIS_VALUES = [
    'RoomAmountOnly',
    'RoomPlusTaxableFees',
    'RoomPlusAllFeesPlusTaxes',
    'RoomPlusSpecificFees',
    'OwnerRevenue',
];
export const FEE_TYPE_VALUES = ['Fixed', 'Percentage', 'Compound'];
export const APPLIES_PER_VALUES = ['Reservation', 'Night', 'Guest', 'Pet', 'Stay'];
export const TAXABLE_STATUS_VALUES = ['Taxable', 'NonTaxable'];
export const FEE_RESPONSIBILITY_VALUES = [
    'GuestCharged',
    'OwnerCharged',
    'SplitAllocationGuestOwner',
    'SplitAllocationCommissionSplit',
    'InternalOnly',
];
export const FEE_ASSIGNMENT_KIND_VALUES = ['SystemWide', 'Property', 'SourceChannel'];
export const COMPOUND_UNIT_VALUES = ['Pet', 'ExtraGuest', 'Vehicle', 'Guest'];
export const COMPOUND_CADENCE_VALUES = ['PerNight', 'PerStay'];
/**
 * Revenue distribution methods accepted as a per-tax / per-fee
 * `distributionMethodOverride` (DS-012 §7.1). The `RevenueDistributionMethod`
 * VO lives in `@artemis/domain` (ADR 0027) — so, unlike the taxes & fees wire
 * enums above (whose VOs live downstream in `apps/api`), this list is imported
 * directly from the domain package rather than duplicated here. Values:
 * `check_in`/`check_out`/`upon_receipt` (Lite), `nightly`/`payments_receivable`
 * (PRO). The Lite-vs-PRO tier gate is a runtime concern (the override only
 * routes a line item; tier enforcement on the org default lives in
 * `UpdateOrgOwnerSettingsUseCase`), so all five are accepted structurally here.
 */
export { REVENUE_DISTRIBUTION_METHOD_VALUES } from '@artemis/domain';
/**
 * Reservation-type internalKeys accepted in a tax's `reservationTypeAllowlist`
 * (SCRUM-272 v1.1, DS-020 §4.2/§11). Like `REVENUE_DISTRIBUTION_METHOD_VALUES`,
 * this key-set lives in `@artemis/domain` (the shared cross-BC primitive,
 * ADR 0027), so it is imported directly rather than duplicated. The wire schema
 * uses it as a `z.enum(...)` so an unknown key is rejected at the HTTP boundary
 * (422) — the same key-set the `TaxApplicability` VO validates against, so a
 * value the aggregate would reject never reaches it.
 */
export { RESERVATION_TYPE_INTERNAL_KEY_VALUES } from '@artemis/domain';
/** Codepoint caps — mirror the domain `_tax-text-policy` / `_fee-text-policy`. */
export const TAX_TEXT_MAX_LENGTH = 120;
export const FEE_TEXT_MAX_LENGTH = 120;
//# sourceMappingURL=enums.js.map